import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import Live2DCanvas from '../components/Live2D/Live2DCanvas';
import TimerOverlay from '../components/TimerOverlay/TimerOverlay';
import ToolButtons from '../components/ToolButtons/ToolButtons';
import Header from '../components/Header/Header';
import ChoiceBox from '../components/ChoiceBox/ChoiceBox';
import BgmModal from '../components/BgmModal/BgmModal';
import BgmMiniPlayer from '../components/BgmMiniPlayer/BgmMiniPlayer';
import MessageBox from '../components/MessageBox/MessageBox';
import { DressModal } from '../components/modals/DressModal';
import HamburgerMenu from '../components/HamburgerMenu/HamburgerMenu';
import AppInfoModal from '../components/modals/AppInfoModal';
import SystemSettingsModal from '../components/modals/SystemSettingsModal';
import RestSettingsModal from '../components/modals/RestSettingsModal';
import StatsModal from '../components/modals/StatsModal';
import { useAppStore } from '../stores/useAppStore';
import { useDressStore } from '../stores/useDressStore';
import { usePomodoroTimer } from '../hooks/usePomodoroTimer';
import { useBackground } from '../hooks/useBackground';
import { useBgm } from '../hooks/useBgm';
import { useMessageSystem } from '../hooks/useMessageSystem';
import { useInitialMessage } from '../hooks/useInitialMessage';
import { useChoice } from '../hooks/useChoice';
import { useWeather } from '../hooks/useWeather';
import { useModal } from '../hooks/useModal';
import { bgmList } from '../data/bgmList';
import { dressList } from '../data/dressList';
import { getNadenadeConversation } from '../data/nadenadeConversations';
import type { Live2DModel } from 'pixi-live2d-display/cubism4';
import { useUserStore } from '../stores/useUserStore';
import './HomePage.css';

const HomePage = () => {
  const { currentSession, pomodoroSettings } = useAppStore();
  const { selectedDress } = useDressStore();
  const { modals, openModal, closeModal, openConfirm, confirmData } = useModal();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [timerOverlayVisible, setTimerOverlayVisible] = useState(false);
  const live2dModelRef = useRef<Live2DModel | null>(null);
  
  // 天気情報
  const { weather } = useWeather();
  
  // BGM管理
  const {
    isBgmPlaying,
    currentBgmIndex,
    bgmProgress,
    bgmList: currentBgmList,
    playBgm,
    stopBgm,
  } = useBgm(bgmList);
  
  // 統合メッセージシステム
  const {
    currentMessage,
    messageVisible,
    showMessageWithMouth,
    showSystemMessage,
    hideMessage,
  } = useMessageSystem(live2dModelRef);
  
  // 選択肢システム（usePomodoroTimerより前に定義する必要がある）
  const {
    isChoiceVisible,
    currentScenario,
    showChoice,
    handleSelect,
    handleTimeout,
  } = useChoice();
  
  // ポモドーロタイマー（メッセージ機能付き）
  const {
    formattedTime,
    isRunning,
    phase,
    handleStart,
    handleStop,
    debugCompleteSession,
  } = usePomodoroTimer(showMessageWithMouth, showChoice);
  
  // 背景画像の管理
  const { backgroundImage } = useBackground({ isCameraActive });
  
  // 初回メッセージ表示（時間帯・訪問回数・天気対応）
  useInitialMessage({
    onShowMessage: showMessageWithMouth,
    weatherCondition: weather.condition
  });
  
  // 休憩終了→WORKフェーズ切り替え時にタイマーオーバーレイを自動表示
  // 注: switchPhase直後はisRunning=false（自動開始は1秒後）なのでisRunningは条件に含めない
  const prevPhaseRef = useRef(phase);
  useEffect(() => {
    if (phase === 'work' && prevPhaseRef.current === 'break') {
      setTimerOverlayVisible(true);
    }
    prevPhaseRef.current = phase;
  }, [phase]);

  // デバッグ用キーボードショートカット（開発環境のみ）
  useEffect(() => {
    if (import.meta.env.DEV) {
      const handleKeyPress = (e: KeyboardEvent) => {
        // Ctrl + Shift + D でセッション即完了
        if (e.ctrlKey && e.shiftKey && e.key === 'D') {
          e.preventDefault();
          debugCompleteSession();
        }
        
        // Ctrl + Shift + M で最大値設定（Max）
        if (e.ctrlKey && e.shiftKey && e.key === 'M') {
          e.preventDefault();
          useUserStore.getState().addDonut(10000);
          useUserStore.getState().addAffinityExp(2000);
          showSystemMessage('デバッグ: 最大値に設定しました！');
        }
        
        // Ctrl + Shift + R でリセット（Reset）
        if (e.ctrlKey && e.shiftKey && e.key === 'R') {
          e.preventDefault();
          const userStore = useUserStore.getState();
          // ドーナツと好感度を初期値に戻す
          userStore.addDonut(-userStore.donuts + 10);
          userStore.addAffinityExp(-userStore.affinityExp);
          showSystemMessage('デバッグ: 初期値にリセットしました');
        }
      };

      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [debugCompleteSession, showSystemMessage]);
  
  // タイマーの進捗率を計算
  const progressPercent = useMemo(() => {
    if (!currentSession.phase) return 0;
    const duration = currentSession.phase === 'work' 
      ? pomodoroSettings.workDuration * 60 
      : pomodoroSettings.breakDuration * 60;
    const elapsed = duration - currentSession.timerRemaining;
    return Math.round((elapsed / duration) * 100);
  }, [currentSession, pomodoroSettings]);

  // タイマーボタンクリック時の処理（オーバーレイを開く）
  const handleTimerClick = () => {
    setTimerOverlayVisible(true);
  };

  // なでなで3回完了後の会話イベント
  const showNadenadeConversation = useCallback(() => {
    const { affinityLevel } = useUserStore.getState();
    const conversation = getNadenadeConversation(affinityLevel);
    
    // 会話の質問を表示（選択肢が消えるまで保持）
    showMessageWithMouth(conversation.question || '', 3000);
    
    // 選択肢を表示
    setTimeout(() => {
      showChoice({
        ...conversation,
        onSelect: (choiceId) => {
          const result = conversation.onSelect?.(choiceId) as any;
          if (result && result.message) {
            // 選択後すぐに結果メッセージを表示（質問メッセージを上書き）
            showMessageWithMouth((result as any).message, 4000);
            
            // 好感度ボーナスを追加
            if ((result as any).affinityBonus) {
              setTimeout(() => {
                useUserStore.getState().addAffinityExp((result as any).affinityBonus);
                // システムメッセージ
                showSystemMessage(`好感度が${(result as any).affinityBonus}上がった！`, 2000);
              }, 4500);
            }
          } else {
            // 結果がない場合のみメッセージを消す
            hideMessage();
          }
        },
        onTimeout: () => {
          hideMessage(); // タイムアウトしたらメッセージを消す
          showMessageWithMouth('...そっか', 3000);
        },
      });
    }, 3500);
  }, [showMessageWithMouth, showSystemMessage, showChoice, hideMessage]);
  
  // グローバル公開は削除（propsで渡すため不要）

  return (
    <div className="app-shell">
    <div
      className="app-container bg-cover bg-center"
      style={{ backgroundImage }}
    >
      {/* Header - Vue版と同じ構成 */}
      <Header
        onToggleMenu={() => setMenuOpen(!menuOpen)}
        menuOpen={menuOpen}
        idle={false}
        weather={weather}
      />
      
      {/* Avatar Area (Live2D) */}
      <div className="avatar-area">
        {/* Live2D Canvas */}
        <Live2DCanvas
          modelPath={dressList.find(d => d.id === selectedDress)?.modelPath || dressList[0].modelPath}
          onModelLoad={(model) => {
            live2dModelRef.current = model;
          }}
          onShowMessage={(message, duration) => {
            showMessageWithMouth(message, duration);
          }}
          onNadenadeComplete={showNadenadeConversation}
        />
        
        {/* ツールボタン群 */}
        <ToolButtons
          idle={false}
          onTimerClick={handleTimerClick}
          onStatsClick={() => openModal('stats')}
          onDressClick={() => openModal('dress')}
          onCameraClick={() => setIsCameraActive(!isCameraActive)}
          isBgmPlaying={isBgmPlaying}
        />
      </div>
      
      {/* メッセージボックス */}
      <MessageBox
        message={currentMessage}
        visible={messageVisible}
      />

      {/* BGMミニプレイヤー */}
      <BgmMiniPlayer
        isPlaying={isBgmPlaying}
        trackName={currentBgmIndex !== null ? currentBgmList[currentBgmIndex]?.name || '' : ''}
        progress={bgmProgress}
        onPlay={() => playBgm(currentBgmIndex ?? 0)}
        onStop={stopBgm}
        onOpenModal={() => openModal('bgm')}
      />
      
      {/* タイマーオーバーレイ（大きなカウントダウン表示） */}
      <TimerOverlay
        visible={timerOverlayVisible}
        onClose={() => setTimerOverlayVisible(false)}
        timerDisplay={formattedTime}
        progressPercent={progressPercent}
        isRunning={isRunning}
        phase={phase}
        onStart={handleStart}
        onStop={handleStop}
      />

      
      {/* BGMモーダル */}
      <BgmModal
        visible={modals.bgm}
        lang="ja"
        bgmList={currentBgmList}
        onClose={() => closeModal('bgm')}
        onPlay={playBgm}
        onShowMessage={(msg) => showMessageWithMouth(msg)}
        onShowConfirm={(msg, cb) => openConfirm(msg, cb)}
      />
      
      {/* 着せ替えモーダル */}
      <DressModal
        visible={modals.dress}
        lang="ja"
        onClose={() => closeModal('dress')}
        onShowMessage={(message) => showMessageWithMouth(message)}
        onShowConfirm={(message, onConfirm) => openConfirm(message, onConfirm)}
      />
      
      {/* ハンバーガーメニュー */}
      <HamburgerMenu 
        menuOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        onSystemSettings={() => openModal('systemSettings')}
        onRestSettings={() => openModal('restSettings')}
        onAppInfo={() => openModal('appInfo')}
      />
      
      {/* アプリ情報モーダル */}
      <AppInfoModal
        visible={modals.appInfo}
        onClose={() => closeModal('appInfo')}
      />
      
      {/* システム設定モーダル */}
      <SystemSettingsModal
        visible={modals.systemSettings}
        onClose={() => closeModal('systemSettings')}
      />
      
      {/* 休息設定モーダル */}
      <RestSettingsModal
        visible={modals.restSettings}
        onClose={() => closeModal('restSettings')}
      />
      
      {/* 統計モーダル */}
      <StatsModal
        visible={modals.stats}
        onClose={() => closeModal('stats')}
      />

      {/* 選択肢システム */}
      <ChoiceBox
        visible={isChoiceVisible}
        question={currentScenario?.question}
        choices={currentScenario?.choices || []}
        onSelect={handleSelect}
        onTimeout={handleTimeout}
        timeoutSeconds={currentScenario?.timeoutSeconds}
      />
      
      {/* 確認ダイアログ */}
      {modals.confirm && confirmData && (
        <div className="fixed inset-0 glass-overlay z-[60] flex items-center justify-center p-4">
          <div className="glass-panel p-6 max-w-sm w-full">
            <p className="text-white/90 mb-5 text-center leading-relaxed">{confirmData.message}</p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  // 連打による二重実行を防ぐため、先にダイアログを閉じてから実行する
                  const { onConfirm } = confirmData;
                  closeModal('confirm');
                  onConfirm();
                }}
                className="flex-1 py-2.5 px-4 glass-btn-primary transition-colors"
              >
                はい
              </button>
              <button
                onClick={() => closeModal('confirm')}
                className="flex-1 py-2.5 px-4 glass-btn transition-colors"
              >
                いいえ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default HomePage;
